<template>
  <v-list-item density="compact" class="ma-0 pa-0">
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="mx-0 my-2 pa-0">
        <h6 class="text-subtitle-1">Advanced Parameters
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Choose the parameters below you want to include in requests to the model.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-if="!hideSamplingParams" cols="6" density="compact" class="ma-0 pa-0">
        <h6 class="text-body-2">Temperature
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="The temperature is randomness of response. Higher number is more random. If setting temperature, don't set Top P">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
      <v-col cols="6" density="compact" class="ma-0 pa-0">
        <h6 class="text-body-2">Max Tokens
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Maximum number of tokens that the agent can respond with. -1 means no limit.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-if="!hideSamplingParams" cols="6" density="compact" class="ma-0 pa-0">
        <v-slider v-model="temperature" @update:model-value="onChange" :min="0.01" :max="1" :step="0.01"
          color="secondary" class="pt-2 pl-1" :disabled="!includeTemperature">
          <template v-slot:append>
            <v-text-field v-model="temperature" @change="onChange" density="compact" style="width: 80px" type="number"
              variant="outlined" hide-details class="smaller-text number" :min="0.01" :max="1" :step="0.01" />
          </template>
        </v-slider>
      </v-col>
      <v-col cols="6" density="compact" class="ma-0 pa-0">
        <v-text-field v-model="maxTokens" density="compact" type="number" variant="outlined" hide-details
          class="mt-2 smaller-text number" :rules="[(v) => v >= -1]" :min="-1" :step="10"
          @change="getNumber(maxTokens) < -1 ? (maxTokens = -1) : null; onChange()" :disabled="!includeMaxTokens" />
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-if="!hideSamplingParams" cols="6" density="compact" class="ma-0 pa-0">
        <h6 class="text-body-2">Top P
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass. So 0.1 means only the tokens comprising the top 10% probability mass are considered. If setting Top P, don't set temperature.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
      <v-col cols="6" density="compact" class="ma-0 pa-0">
        <h6 class="text-body-2">Top K
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Only sample from the top K options for each subsequent token. Used to remove long tail low probability responses.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col v-if="!hideSamplingParams" cols="6" density="compact" class="ma-0 pa-0">
        <v-slider v-model="topP" @update:model-value="onChange" :min="0.01" :max="1" :step="0.01" color="secondary"
          class="pt-2 pl-1" :disabled="!includeTopP">
          <template v-slot:append>
            <v-text-field v-model="topP" @change="onChange" density="compact" style="width: 80px" type="number"
              variant="outlined" hide-details class="smaller-text number" :min="0.01" :max="1" :step="0.01" />
          </template>
        </v-slider>
      </v-col>
      <v-col cols="6" density="compact" class="ma-0 pa-0">
        <v-text-field v-model="topK" density="compact" type="number" variant="outlined" hide-details
          class="mt-2 smaller-text number" :rules="[(v) => v >= -1]" :min="0" :step="10"
          @change="getNumber(topK) < 0 ? (topK = 0) : null; onChange()" :disabled="!includeTopK" />
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="4" density="compact" class="ma-0 pa-0">
        <h6 class="text-body-2">Seed
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Attempt deterministic responses. Same seed & other parameters will produce same response. Doesn't always work.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
      <v-col cols="8" density="compact" class="ma-0 py-0 pl-2 pr-0">
        <h6 class="text-body-2">Stop
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Up to 4 words or strings that will cause the model to stop generating text. If multiple, use a comma to separate.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="4" density="compact" class="ma-0 pa-0">
        <v-text-field v-model="seed" density="compact" type="number" variant="outlined" hide-details
          class="mt-2 smaller-text number" :rules="[(v) => v >= 0]" :min="0" :step="10"
          @change="getNumber(seed) < 0 ? (seed = 0) : null; onChange()" :disabled="!includeSeed" />
      </v-col>
      <v-col cols="8" density="compact" class="ma-0 py-0 pl-2 pr-0">
        <v-text-field v-model="stop" density="compact" variant="outlined" hide-details class="mt-2 smaller-text"
          :disabled="!includeStop" />
      </v-col>
    </v-row>
    <template v-if="isGpt5Model">
      <v-row density="compact" class="ma-0 pa-0">
        <v-col cols="6" density="compact" class="ma-0 pa-0">
          <h6 class="text-body-2">Reasoning Effort
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Controls how much reasoning the model performs. GPT-5 only.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
          </h6>
        </v-col>
        <v-col cols="6" density="compact" class="ma-0 pa-0">
          <h6 class="text-body-2">Verbosity
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Constrains the verbosity of the model's response. GPT-5 only.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
          </h6>
        </v-col>
      </v-row>
      <v-row density="compact" class="ma-0 pa-0">
        <v-col cols="6" density="compact" class="ma-0 pa-0">
          <v-select
            v-model="reasoningEffort"
            :items="['none', 'minimal', 'low', 'medium', 'high', 'xhigh']"
            density="compact"
            variant="outlined"
            hide-details
            class="mt-2"
            label="Reasoning Effort"
            :disabled="!includeReasoningEffort"
            @update:model-value="onChange()"
          />
        </v-col>
        <v-col cols="6" density="compact" class="ma-0 pa-0">
          <v-select
            v-model="verbosity"
            :items="['low', 'medium', 'high']"
            density="compact"
            variant="outlined"
            hide-details
            class="mt-2"
            label="Verbosity"
            :disabled="!includeVerbosity"
            @update:model-value="onChange()"
          />
        </v-col>
      </v-row>
    </template>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="mx-0 my-2 pa-0">
        <h6 class="text-subtitle-2">Include Parameters
          <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300" text="Toggle the switches below to include or exclude parameters in requests to the model. This will serve as
          the default for agents that use this model, but can be overridden per agent.">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="x-small">mdi-information-outline</v-icon>
            </template>
          </v-tooltip>
        </h6>
      </v-col>
    </v-row>
    <v-row density="compact" class="ma-0 pa-0">
      <v-col cols="12" density="compact" class="ma-0 py-0 pl-2 pr-0">
        <v-switch v-if="!hideSamplingParams" v-model="includeTemperature" label="Temperature" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('temperature')" />
        <v-switch v-model="includeMaxTokens" label="Max Tokens" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('maxTokens')" />
        <v-switch v-if="!hideSamplingParams" v-model="includeTopP" label="Top P" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('topP')" />
        <v-switch v-model="includeTopK" label="Top K" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('topK')" />
        <v-switch v-model="includeSeed" label="Seed" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('seed')" />
        <v-switch v-model="includeStop" label="Stop" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-0"
          density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('stop')" />
        <template v-if="isGpt5Model">
          <v-switch v-model="includeReasoningEffort" label="Reasoning" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-3"
            density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('reasoningEffort')" />
          <v-switch v-model="includeVerbosity" label="Verbosity" hide-details inline inset class="model-option-switch d-inline-block ml-0 mr-0"
            density="compact" color="secondary" base-color="surface-variant" @change="toggleChange('verbosity')" />
        </template>
      </v-col>
    </v-row>
  </v-list-item>
</template>
<script lang="ts">
import { ModelOptions } from '@/types/models'
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'

@Options({
  props: {
    modelOptionsInput: <ModelOptions>{},
    modelName: {
      type: String,
      default: ''
    },
    // The selected model rejects temperature/top_p (Anthropic 400s) — hide those controls entirely.
    hideSamplingParams: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['showGlobalTooltips'])
  },
  watch: {
    modelName() {
      ;(this as ModelOptionsChoices).handleModelNameChange()
    }
  }
})
export default class ModelOptionsChoices extends Vue {
  public inheritAttrs = false
  public showGlobalTooltips!: boolean
  public modelOptionsInput!: ModelOptions
  public modelName!: string
  public hideSamplingParams!: boolean
  public temperature: string | number = 0.1
  public includeTemperature: boolean = false
  public maxTokens: string | number = -1
  public includeMaxTokens: boolean = false
  public topK: string | number = 1
  public includeTopK: boolean = false
  public topP: string | number = 1
  public includeTopP: boolean = false
  public seed: string | number = 0
  public includeSeed: boolean = false
  public stop: string = ''
  public includeStop: boolean = false
  public reasoningEffort: string = 'medium'
  public includeReasoningEffort: boolean = false
  public verbosity: string = 'medium'
  public includeVerbosity: boolean = false
  opposites: string[][] = [
    ['temperature', 'topP']
  ]

  get isGpt5Model(): boolean {
    return typeof this.modelName === 'string' && this.modelName.includes('gpt-5')
  }

  get modelOptions(): ModelOptions {
    const { maxTokens, temperature, topK, topP, seed, stop, reasoningEffort, verbosity } = this
    const included: ModelOptions = {}
    if (this.includeTemperature) {
      included.temperature = this.getNumber(temperature)
    }
    if (this.includeMaxTokens) {
      included.maxTokens = this.getNumber(maxTokens)
    }
    if (this.includeTopK) {
      included.topK = this.getNumber(topK)
    }
    if (this.includeTopP) {
      included.topP = this.getNumber(topP)
    }
    if (this.includeSeed) {
      included.seed = this.getNumber(seed)
    }
    if (this.includeStop) {
      included.stop = stop.split(',').map(s => s.trim())
    }
    if (this.isGpt5Model && this.includeReasoningEffort) {
      included.reasoningEffort = reasoningEffort as ModelOptions['reasoningEffort']
    }
    if (this.isGpt5Model && this.includeVerbosity) {
      included.verbosity = verbosity as ModelOptions['verbosity']
    }
    return included
  }

  public getNumber(value: string | number): number {
    if (typeof value === 'string') {
      if (value.includes('.')) {
        return parseFloat(value)
      }
      return parseInt(value, 10)
    }
    return value
  }

  public mounted() {
    if (this.modelOptionsInput != null) {
      for (const key of Object.keys(this.modelOptionsInput)) {
        if (this.modelOptionsInput[key as keyof ModelOptions] !== undefined) {
          this.setParam(key, this.modelOptionsInput[key as keyof ModelOptions])
        }
      }
    }
    this.handleModelNameChange()
  }

  private setParam(param: string, value: any): void {
    switch (param) {
      case 'temperature':
        this.temperature = value
        this.includeTemperature = true
        break
      case 'maxTokens':
        this.maxTokens = value
        this.includeMaxTokens = true
        break
      case 'topP':
        this.topP = value
        this.includeTopP = true
        break
      case 'topK':
        this.topK = value
        this.includeTopK = true
        break
      case 'seed':
        this.seed = value
        this.includeSeed = true
        break
      case 'stop':
        this.stop = value
        this.includeStop = true
        break
      case 'reasoningEffort':
        this.reasoningEffort = value
        this.includeReasoningEffort = true
        break
      case 'verbosity':
        this.verbosity = value
        this.includeVerbosity = true
        break
      default:
        console.error(`Unknown variable ${param}`)
    }
  }

  private getIncludeParam(param: string): boolean {
    switch (param) {
      case 'temperature': return this.includeTemperature
      case 'maxTokens': return this.includeMaxTokens
      case 'topP': return this.includeTopP
      case 'topK': return this.includeTopK
      case 'seed': return this.includeSeed
      case 'stop': return this.includeStop
      case 'reasoningEffort': return this.includeReasoningEffort
      case 'verbosity': return this.includeVerbosity
    }
    console.error(`Unknown variable ${param}`)
    return false
  }

  private setIncludeParam(param: string, value: boolean): void {
    switch (param) {
      case 'temperature':
        this.includeTemperature = value
        break
      case 'maxTokens':
        this.includeMaxTokens = value
        break
      case 'topP': 
        this.includeTopP = value
        break
      case 'topK':
        this.includeTopK = value
        break
      case 'seed':
        this.includeSeed = value
        break
      case 'stop':
        this.includeStop = value 
        break
      case 'reasoningEffort':
        this.includeReasoningEffort = value
        break
      case 'verbosity':
        this.includeVerbosity = value
        break
      default:
        console.error(`Unknown variable ${param}`)
    }
  }

  private handleModelNameChange(): void {
    if (!this.modelName || this.modelName.trim().length === 0) return
    if (this.isGpt5Model) return
    if (this.includeReasoningEffort || this.includeVerbosity) {
      this.includeReasoningEffort = false
      this.includeVerbosity = false
      this.onChange()
    }
  }

  public toggleChange(variable: string) {
    for (const [side1, side2] of this.opposites) {
      if (variable === side1) {
        if (this.getIncludeParam(side1) && this.getIncludeParam(side2)) {
          this.setIncludeParam(side2, false)
        }
      }
      if (variable === side2) {
        if (this.getIncludeParam(side1) && this.getIncludeParam(side2)) {
          this.setIncludeParam(side1, false)
        }
      }
    }
    this.onChange()
  }

  public onChange() {
    this.$emit('update', this.modelOptions)
  }
}
</script>
<style lang="scss" ::v-deep>
  .smaller-text.number .v-field__input {
    padding-inline: 8px  2px;
  }
  .toggle {
    display: inline;
  }
  .model-option-switch .v-switch__track {
    opacity: 1;
    background-color: rgba(var(--v-theme-surface-variant), 0.9);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.16);
  }
  .model-option-switch .v-selection-control--dirty .v-switch__track {
    background-color: rgb(var(--v-theme-secondary));
    border-color: rgb(var(--v-theme-secondary));
  }
</style>
